import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';
import * as jsonManager from '../../abis/Manager.json';
import { environment } from '../../../environments/environment';
import { ConnectionsService } from '../../services/connectionsWeb3/connections.service';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { PaymentService } from '../../services/payment.service';
import { UserExternalInvestDto } from 'src/app/interfaces/user-external-invest.dto';
import { UserTransferDto } from '../../interfaces/user-transfer.dto';
import { DigitalWalletService } from '../../services/digital-wallet.service';
import { TwoFactorAuthService } from '../../services/two-factor/two-factor-auth.service';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-transfer-batch',
  templateUrl: './transfer-batch.component.html',
  styleUrls: ['./transfer-batch.component.css'],
})
export class TransferBatchComponent implements OnInit {
  formTransferBatch: FormGroup = this.createFormTransferBatch();

  ourAddress: string | undefined;
  web3: any;
  abiManager: any = jsonManager;
  contractManager: any;
  tokenToSellerMap: { [tokenAddress: string]: string } = {};
  mappingId = new Map();

  availableWallets: {
    id: string;
    name: string;
  }[] = [];
  transfers: UserTransferDto[] = [];
  selectedWalletId: string | undefined;
  verified: boolean = false;
  transferred: boolean = false;
  blockVerify: boolean = true;
  blockTransfer: boolean = true;
  has2FAEnabled: boolean = false;
  show2FAModal: boolean = false;
  last2FACode: string = '';

  constructor(
    private readonly connSrv: ConnectionsService,
    private readonly paymentService: PaymentService,
    private readonly walletService: DigitalWalletService,
    private readonly twoFactorService: TwoFactorAuthService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly afs: AngularFirestore
  ) {
    this.web3 = this.connSrv.web3Instance;

    this.connSrv.addressUser.subscribe((res: string) => {
      this.ourAddress = res;
    });

    this.contractManager = new this.web3.eth.Contract(
      this.abiManager.abi,
      environment.config.ADDRESS_MANAGER
    );
  }

  ngOnInit() {
    this.loadWallets();
    this.check2FAStatus();

    this.wallet?.valueChanges.subscribe((selectedWallet) => {
      const wallet = this.availableWallets.find(
        (wallet) => wallet.id === selectedWallet
      );
      this.selectedWalletId = wallet?.id;
      this.verifyStatus(true);
    });
  }

  verifyStatus(forceChange: boolean = false) {
    if (forceChange) {
      this.verified = false;
      this.transferred = false;
    }
    this.blockVerify =
      !this.selectedWalletId || this.transfers.length == 0 || this.verified;
    this.blockTransfer =
      !this.selectedWalletId ||
      this.transfers.length == 0 ||
      !this.verified ||
      this.transferred;
  }

  createFormTransferBatch() {
    return new FormGroup({
      wallet: new FormControl('', [Validators.required]),
    });
  }

  get wallet() {
    return this.formTransferBatch?.get('wallet');
  }

  onListTransfersChange(transfers: UserTransferDto[]) {
    this.transfers = transfers;
    this.verifyStatus(true);
  }

  private loadWallets() {
    this.availableWallets = [
      {
        id: '1',
        name: 'Dividendos',
      },
      {
        id: '3',
        name: 'Referidos',
      },
    ];
  }

  formatToWei(num: number) {
    return this.web3.utils.toWei(num.toString(), 'ether');
  }

  getCheckText(error: string = ''): string {
    if (error === 'no configurado') {
      return 'Billetera del sistema no configurada';
    } else if (error === 'no existe') {
      return 'Billetera del sistema no existe en Mangopay';
    } else if (error === 'no fondos') {
      return 'No hay saldo suficiente para honrar las transferencias';
    } else if (error === 'user error') {
      return 'Errores en la lista de solicitudes';
    } else return '';
  }

  // Función para verificar si un usuario existe en Firestore
  async verifyUserExists(email: string): Promise<boolean> {
    try {
      const userQuery = await this.afs.collection('users', ref =>
        ref.where('email', '==', email).limit(1)
      ).get().toPromise();

      return !!(userQuery && !userQuery.empty);
    } catch (error) {
      console.error('Error verificando usuario en Firestore:', error);
      return false;
    }
  }

  async checkTransfers() {
    Swal.fire({
      title: 'Verificando...',
      html: 'No cierres esta pantalla!!',
      allowEscapeKey: false,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      // Verificar que el usuario esté autenticado
      const currentUser = this.authService.userAuth.getValue();
      console.log('[checkTransfers] Usuario autenticado:', currentUser ? 'Sí' : 'No');
      
      if (!currentUser) {
        Swal.close();
        Swal.fire({
          title: 'Error de autenticación',
          text: 'Debes estar autenticado para realizar esta operación.',
          icon: 'error',
        });
        return;
      }

      console.log('[checkTransfers] Verificando transferencias:', this.transfers);
      console.log('[checkTransfers] Wallet seleccionada:', this.selectedWalletId);

      // Verificar usuarios en Firestore antes de enviar al backend
      for (let transfer of this.transfers) {
        const userExists = await this.verifyUserExists(transfer.email);
        console.log(`[checkTransfers] Usuario ${transfer.email} existe en Firestore:`, userExists);

        if (!userExists) {
          transfer.error = 'unknown';
        }
      }

      console.log('[checkTransfers] Enviando petición al backend...');
      const result = await this.walletService.checkBatchTransfers(
        this.transfers,
        this.selectedWalletId!
      );

      console.log('[checkTransfers] Resultado de verificación:', result);

      Swal.close();

      this.transfers = result.transfers;

      if (result.error === '') {
        this.verified = true;
        Swal.fire({
          title: 'Verificación exitosa',
          text: 'Las inversiones han sido verificadas correctamente.',
          icon: 'success',
        });
      } else {
        Swal.fire({
          title: 'Error de verificación',
          text: this.getCheckText(result.error),
          icon: 'error',
        });
      }
    } catch (error) {
      console.error('Error al verificar inversiones:', error);
      Swal.close();
      Swal.fire({
        title: 'Error',
        text: 'Ocurrió un error al verificar las inversiones.',
        icon: 'error',
      });
      return;
    }

    this.verifyStatus();
  }

  async processTransfers() {
    // Verificar si el usuario tiene 2FA habilitado
    if (!this.has2FAEnabled) {
      // Mostrar mensaje de que debe activar 2FA
      const result = await Swal.fire({
        title: '2FA Requerido',
        html: `
          <div class="text-center">
            <i class="bi bi-shield-exclamation text-warning" style="font-size: 3rem;"></i>
            <p class="mt-3">Para realizar transferencias masivas, debes tener activada la autenticación de dos factores (2FA).</p>
            <p class="text-muted">Esta es una medida de seguridad obligatoria para operaciones críticas.</p>
          </div>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Activar 2FA',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#f09502',
        cancelButtonColor: '#6c757d'
      });

      if (result.isConfirmed) {
        // Redirigir directamente a la configuración de 2FA
        console.log('Navegando a configuración 2FA (sin 2FA habilitado)...');

        // Redirigir a configuración
        this.router.navigate(['/perfil/configuracion']);
      }
      return;
    }

    // Si tiene 2FA, mostrar modal de verificación
    this.show2FAModal = true;
  }

  // Métodos para 2FA
  async check2FAStatus() {
    try {
      const currentUser = this.authService.userAuth.getValue();
      if (currentUser?.uid) {
        this.has2FAEnabled = await this.twoFactorService.isEnabled(currentUser.uid);
      }
    } catch (error) {
      console.error('Error checking 2FA status:', error);
      this.has2FAEnabled = false;
    }
  }


  async executeTransfers() {
    const confirmResult = await Swal.fire({
      title: 'Procesar transferencias',
      html: `¿Estás seguro de que quieres procesar las transferencias? Este proceso no es reversible.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, procesar',
      cancelButtonText: 'Cancelar',
    });

    if (!confirmResult.isConfirmed) {
      return; // Usuario canceló la operación
    }

    // Validar que el código 2FA esté presente antes de enviar
    if (!this.last2FACode || this.last2FACode.length !== 6) {
      Swal.close();
      Swal.fire({
        title: 'Error',
        text: 'El código 2FA no es válido. Por favor, verifica nuevamente.',
        icon: 'error',
      });
      return;
    }

    console.log('[executeTransfers] Enviando transferencias con código 2FA:', this.last2FACode);
    console.log('[executeTransfers] Transfers:', this.transfers);
    console.log('[executeTransfers] Wallet ID:', this.selectedWalletId);

    Swal.fire({
      title: 'Procesando...',
      html: 'No cierres esta pantalla!!',
      allowEscapeKey: false,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    const result = await this.walletService.processBatchTransfers(
      this.transfers,
      this.selectedWalletId!,
      this.last2FACode
    );

    Swal.close();

    console.log('Resultado de la verificación:', result);

    if (result.error === '') {
      this.transferred = true;
      this.verifyStatus();
      Swal.fire({
        title: 'Transferencias exitosas',
        text: 'Los fondos han sido transferidos correctamente.',
        icon: 'success',
        showCancelButton: true,
        confirmButtonText: 'Configurar 2FA',
        cancelButtonText: 'Cerrar',
        confirmButtonColor: '#f09502',
        cancelButtonColor: '#6c757d'
      }).then((result) => {
        if (result.isConfirmed) {
          // Redirigir directamente a la configuración de 2FA después de transferencia exitosa
          console.log('Navegando a configuración 2FA...');

          // Redirigir a configuración
          this.router.navigate(['/perfil/configuracion']);
        }
      });
    } else {
      this.transfers = result.transfers;
      Swal.fire({
        title: 'Error en las transferencias',
        text: 'No se pudieron procesar las transferencias de todos los usuarios',
        icon: 'error',
      });
    }
    this.verifyStatus();
  }

  on2FAVerified(success: boolean) {
    console.log('[on2FAVerified] Evento recibido, success:', success);
    console.log('[on2FAVerified] Código 2FA guardado:', this.last2FACode ? 'Sí' : 'No');
    this.show2FAModal = false;
    if (success) {
      // Verificar que el código 2FA esté guardado antes de proceder
      if (!this.last2FACode || this.last2FACode.length !== 6) {
        console.error('[on2FAVerified] Error: Código 2FA no válido o no guardado');
        Swal.fire({
          title: 'Error',
          text: 'El código 2FA no se guardó correctamente. Por favor, intenta de nuevo.',
          icon: 'error',
        });
        return;
      }
      console.log('[on2FAVerified] Ejecutando transferencias con código:', this.last2FACode);
      // Proceder con las transferencias después de verificar 2FA
      this.executeTransfers();
    } else {
      console.log('[on2FAVerified] Verificación fallida, no se ejecutan transferencias');
    }
  }

  on2FACodeReceived(code: string) {
    console.log('[on2FACodeReceived] Código recibido:', code);
    this.last2FACode = code;
    console.log('[on2FACodeReceived] Código guardado en last2FACode:', this.last2FACode);
  }

  on2FACancelled() {
    this.show2FAModal = false;
    // No hacer nada, el usuario canceló la operación
  }
}
